import React, { useEffect, useRef, useState } from "react";
import { BiChevronDown } from "react-icons/bi";
import { AiOutlineSearch } from "react-icons/ai";
import { TailSpin } from "react-loader-spinner";

export interface AsyncOption {
  value: string;
  label: string;
}

interface AsyncSearchSelectProps {
  /** Called (debounced) with the current search term; should resolve the option list */
  fetchOptions: (search: string) => Promise<AsyncOption[]>;
  /** Currently selected option value */
  value?: string;
  onChange: (option: AsyncOption | null) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
}

const AsyncSearchSelect = ({
  fetchOptions,
  value,
  onChange,
  placeholder = "Select",
  debounceMs = 400,
  className,
}: AsyncSearchSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<AsyncOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");

  const wrapperRef = useRef<HTMLDivElement>(null);
  const fetchRef = useRef(fetchOptions);

  useEffect(() => {
    fetchRef.current = fetchOptions;
  });

  // Keep the displayed label in sync when value is cleared externally
  useEffect(() => {
    if (!value) setSelectedLabel("");
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced fetch whenever the dropdown is open and the query changes
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const timer = setTimeout(async () => {
      const result = await fetchRef.current(query);
      setOptions(result || []);
      setLoading(false);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, open, debounceMs]);

  const handleSelect = (option: AsyncOption) => {
    setSelectedLabel(option.label);
    onChange(option);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={`relative ${className ?? ""}`} ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-[13px] flex items-center justify-between gap-2 outline-none hover:border-gray-300">
        <span className={selectedLabel ? "truncate" : "text-gray-400 truncate"}>
          {selectedLabel || placeholder}
        </span>
        <BiChevronDown size={16} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="flex items-center gap-1 px-2 border-b border-gray-100">
            <AiOutlineSearch size={16} className="text-gray-400 shrink-0" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="flex-1 py-2 text-[13px] outline-none bg-transparent"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {loading ? (
              <li className="flex justify-center py-4">
                <TailSpin color="skyblue" height={22} width={22} />
              </li>
            ) : options.length === 0 ? (
              <li className="px-3 py-2 text-[13px] text-gray-400">No results</li>
            ) : (
              options.map((option) => (
                <li
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  title={option.label}
                  className={`px-3 py-2 text-[13px] cursor-pointer hover:bg-[#E8F7FA] truncate ${
                    option.value === value ? "bg-[#E8F7FA] text-[#0979A1] font-medium" : ""
                  }`}>
                  {option.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AsyncSearchSelect;
